import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import baseURL from './url';
import { getTenantSlug } from './paths';

const DataContext = createContext();

let bootstrapCache = {};

function fetchBootstrap() {
    return fetch(`${baseURL}/bootstrapGet.php`)
        .then(r => r.json());
}

export function pedirBootstrap(forzar = false) {
    const slug = getTenantSlug();
    if (forzar || !bootstrapCache[slug]) {
        bootstrapCache[slug] = fetchBootstrap().catch(err => {
            delete bootstrapCache[slug];
            throw err;
        });
    }
    return bootstrapCache[slug];
}

export function DataProvider({ children, autoLoad = [] }) {
    const [productos, setProductos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [contacto, setContacto] = useState(null);
    const [banners, setBanners] = useState([]);
    const [mesas, setMesas] = useState([]);
    const [codigos, setCodigos] = useState([]);
    const [metodos, setMetodos] = useState([]);
    const [horarios, setHorarios] = useState([]);
    const [slugNoEncontrado, setSlugNoEncontrado] = useState(false);
    const [loading, setLoading] = useState(true);
    const cargarProductos = useCallback(() => {
        return fetch(`${baseURL}/productosGet.php`)
            .then(r => r.json())
            .then(data => { setProductos(data.productos || []); return data.productos || []; })
            .catch(err => { console.error(err); return []; });
    }, []);

    const cargarCategorias = useCallback(() => {
        return fetch(`${baseURL}/categoriasGet.php`)
            .then(r => r.json())
            .then(data => { setCategorias(data.categorias || []); return data.categorias || []; })
            .catch(err => { console.error(err); return []; });
    }, []);

    const cargarContacto = useCallback(() => {
        return fetch(`${baseURL}/contactoGet.php`)
            .then(r => r.json())
            .then(data => { setContacto(data.contacto || null); return data.contacto || null; })
            .catch(err => { console.error(err); return null; });
    }, []);

    const cargarBanners = useCallback(() => {
        return fetch(`${baseURL}/bannersGet.php`)
            .then(r => r.json())
            .then(data => { setBanners(data.banners || []); return data.banners || []; })
            .catch(err => { console.error(err); return []; });
    }, []);

    const cargarMesas = useCallback(() => {
        return fetch(`${baseURL}/mesasGet.php`)
            .then(r => r.json())
            .then(data => { setMesas(data.mesas || []); return data.mesas || []; })
            .catch(err => { console.error(err); return []; });
    }, []);

    const cargarCodigos = useCallback(() => {
        return fetch(`${baseURL}/codigosGet.php`)
            .then(r => r.json())
            .then(data => { setCodigos(data.codigos || []); return data.codigos || []; })
            .catch(err => { console.error(err); return []; });
    }, []);

    const cargarMetodos = useCallback(() => {
        return fetch(`${baseURL}/metodosGet.php`)
            .then(r => r.json())
            .then(data => { setMetodos(data.metodos || []); return data.metodos || []; })
            .catch(err => { console.error(err); return []; });
    }, []);

    const cargarHorarios = useCallback(() => {
        return fetch(`${baseURL}/horariosGet.php`)
            .then(r => r.json())
            .then(data => { setHorarios(data.horarios || []); return data.horarios || []; })
            .catch(err => { console.error(err); return []; });
    }, []);

    const cargarBootstrap = useCallback((forzar = false) => {
        return pedirBootstrap(forzar)
            .then(data => {
                if (data && data.no_encontrado) {
                    setSlugNoEncontrado(true);
                    return;
                }
                if (data.productos) setProductos(data.productos);
                if (data.categorias) setCategorias(data.categorias);
                if (data.contacto !== undefined) setContacto(data.contacto || null);
                if (data.banners) setBanners(data.banners);
                if (data.codigos) setCodigos(data.codigos);
                if (data.metodos) setMetodos(data.metodos);
                if (data.horarios) setHorarios(data.horarios);
                if (data.mesas) setMesas(data.mesas);
            })
            .catch(err => { console.error(err); })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (autoLoad.includes('bootstrap')) {
            cargarBootstrap();
            return;
        }

        const fetchers = [];
        if (autoLoad.includes('productos')) fetchers.push(cargarProductos());
        if (autoLoad.includes('categorias')) fetchers.push(cargarCategorias());
        if (autoLoad.includes('contacto')) fetchers.push(cargarContacto());
        if (autoLoad.includes('banners')) fetchers.push(cargarBanners());
        if (autoLoad.includes('mesas')) fetchers.push(cargarMesas());
        if (autoLoad.includes('codigos')) fetchers.push(cargarCodigos());
        if (autoLoad.includes('metodos')) fetchers.push(cargarMetodos());
        if (autoLoad.includes('horarios')) fetchers.push(cargarHorarios());

        if (fetchers.length > 0) {
            Promise.all(fetchers).finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <DataContext.Provider value={{
            productos, categorias, contacto, banners, mesas, codigos, metodos, horarios, loading, slugNoEncontrado,
            cargarProductos, cargarCategorias, cargarContacto, cargarBanners, cargarMesas, cargarCodigos, cargarMetodos, cargarHorarios, cargarBootstrap,
        }}>
            {children}
        </DataContext.Provider>
    );
}

export function useData() {
    return useContext(DataContext);
}

export default DataContext;
